import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

export default function ImageCesium({ viewer, latestSurvey, apiBase }) {
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);

  // We use a DataSource for easy management of Entities
  const dataSourceRef = useRef(null);
  const handlerRef = useRef(null);
  const hoverHandlerRef = useRef(null);

  // Helper: image/painting icon inside white circle
  const makeImageDataUrl = (size = 42, circleFill = "#ffffff", iconColor = "#222") => {
    const radius = size / 2;
    const strokeW = Math.max(1, size * 0.06);
    const pad = size * 0.24;
    const frameX = pad;
    const frameY = pad;
    const frameW = size - pad * 2;
    const frameH = size - pad * 2;

    const mountainLeft = frameX + frameW * 0.12;
    const mountainBaseY = frameY + frameH * 0.82;
    const mountainPeak1X = frameX + frameW * 0.38;
    const mountainPeak1Y = frameY + frameH * 0.40;
    const mountainMidX = frameX + frameW * 0.56;
    const mountainMidY = frameY + frameH * 0.62;
    const mountainPeak2X = frameX + frameW * 0.74;
    const mountainPeak2Y = frameY + frameH * 0.34;
    const mountainRightX = frameX + frameW * 0.88;

    const sunCX = frameX + frameW * 0.74;
    const sunCY = frameY + frameH * 0.28;
    const sunR = Math.max(1.4, size * 0.045);

    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
        <defs>
          <filter id="s" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="0.8" stdDeviation="1" flood-color="#000" flood-opacity="0.12"/>
          </filter>
        </defs>
        <g filter="url(#s)">
          <circle cx="${radius}" cy="${radius}" r="${radius - 1}" fill="${circleFill}" stroke="#e6e6e6" stroke-width="1" />
        </g>
        <g transform="translate(0,0)" fill="none" stroke="${iconColor}" stroke-linecap="round" stroke-linejoin="round" stroke-width="${strokeW}">
          <rect x="${frameX}" y="${frameY}" width="${frameW}" height="${frameH}" rx="${Math.max(1, size * 0.03)}" fill="none" />
        </g>
        <g transform="translate(0,0)" fill="${iconColor}">
          <path d="M ${mountainLeft} ${mountainBaseY} L ${mountainPeak1X} ${mountainPeak1Y} L ${mountainMidX} ${mountainMidY} L ${mountainPeak2X} ${mountainPeak2Y} L ${mountainRightX} ${mountainBaseY} Z" />
          <path d="M ${mountainPeak1X + frameW * 0.05} ${mountainPeak1Y + frameH * 0.10} L ${mountainPeak1X + frameW * 0.10} ${mountainPeak1Y - frameH * 0.03} L ${mountainPeak1X + frameW * 0.18} ${mountainPeak1Y + frameH * 0.12} Z" fill="${circleFill}" />
          <circle cx="${sunCX}" cy="${sunCY}" r="${sunR}" />
        </g>
      </svg>
    `;
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  };

  // 1. Fetch Images & Prepare IDs
  useEffect(() => {
    if (!latestSurvey) return;

    const fetchImages = async () => {
      try {
        const loc = latestSurvey.location_name || latestSurvey.name;
        const sv = latestSurvey.survey_time || latestSurvey.survey_date;

        const response = await axios.get(`${apiBase}/ground_img/discovery`, {
          params: { location_name: loc, survey_time: sv },
        });

        if (response.data && response.data.found) {
          const found = response.data.found;

          const p1 = loc;
          const p2 = sv && sv.includes("T") ? sv.split("T")[0] : sv;
          const slugify = (s) => (s ? s.toString().replace(/[^a-z0-9]/gi, "-").toLowerCase() : "");

          // --- Process items to add unique IDs and full URLs ---
          const processedImages = found.map((img) => {
            const meta = img.file?.metadata || {};
            const layerName = img.layer || "images";
            const fileName = img.itemname;
            // Unique ID for Sidebar & Map Entity
            const id = `groundimg-${layerName}-${fileName}`; 

            const locSlug = slugify(p1);
            const svSlug = slugify(p2);
            let imageUrl = `${apiBase}/ground_img/${locSlug}/${svSlug}`;
            if (img.layer) imageUrl += `/${img.layer}`;
            imageUrl += `/${img.itemname}`;

            return {
              ...img,
              uniqueId: id, // Store ID in state
              fullUrl: imageUrl,
              meta: {
                ...meta,
                location_name: p1,
                survey_time: p2,
                isAutoDiscovered: true,
                layer: img.layer,
              }
            };
          });

          setImages(processedImages);

          // --- Map to sidebar format ---
          const layersResolved = processedImages.map((img) => ({
            id: img.uniqueId,
            title: img.itemname,
            name: img.itemname,
            url: img.fullUrl,
            visible: true,
            opacity: 1.0,
            meta: img.meta,
          }));

          window.dispatchEvent(
            new CustomEvent("ground-images-discovered", {
              detail: {
                location: p1,
                survey_time: p2,
                full_layers: layersResolved,
              },
            })
          );
        } else {
          setImages([]);
        }
      } catch (err) {
        console.error("[ImageCesium] Error loading images:", err);
      }
    };

    fetchImages();
  }, [latestSurvey, apiBase]);

  // 2. Plot Icons (Entities with billboard)
  useEffect(() => {
    if (!viewer || !images.length) return;
    const Cesium = window.Cesium;

    // A. Cleanup previous data source
    if (dataSourceRef.current) {
      try { viewer.dataSources.remove(dataSourceRef.current); } catch (e) {}
      dataSourceRef.current = null;
    }

    // B. Create new DataSource
    const dataSource = new Cesium.CustomDataSource("GroundImages");
    viewer.dataSources.add(dataSource);
    dataSourceRef.current = dataSource;

    let minLat = 90, maxLat = -90, minLon = 180, maxLon = -180;
    let hasValidCoords = false;

    const iconDataUrl = makeImageDataUrl(42, "#ffffff", "#222");
    console.log(`[ImageCesium] plotting ${images.length} icons...`);

    images.forEach((img) => {
      const meta = img.meta || {};

      if (meta.latitude && meta.longitude) {
        hasValidCoords = true;

        const lat = parseFloat(meta.latitude);
        const lon = parseFloat(meta.longitude);
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
        if (lon < minLon) minLon = lon;
        if (lon > maxLon) maxLon = lon;

        // --- ADD ENTITY (with specific ID) ---
        dataSource.entities.add({
          id: img.uniqueId, // IMPORTANT: Assign the ID here
          position: Cesium.Cartesian3.fromDegrees(lon, lat, 0),
          billboard: {
            image: iconDataUrl,
            verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
            heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
            scale: 0.9,
          },
          properties: {
            type: "GROUND_IMAGE",
            url: img.fullUrl,
            name: img.itemname,
            meta: meta,
          },
        });
      }
    });

    // 3. Auto-Zoom
    if (hasValidCoords) {
      if (minLat === maxLat && minLon === maxLon) {
        viewer.camera.flyTo({ destination: Cesium.Cartesian3.fromDegrees(minLon, minLat, 500), duration: 2.0 });
      } else {
        const rectangle = Cesium.Rectangle.fromDegrees(minLon, minLat, maxLon, maxLat);
        viewer.camera.flyTo({
          destination: rectangle,
          duration: 2.5,
          offset: new Cesium.HeadingPitchRange(0, -Cesium.Math.PI_OVER_FOUR, 1500),
        });
      }
    }

    viewer.scene.requestRender();

    // 4. Handlers (Click/Hover)
    handlerRef.current = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
    handlerRef.current.setInputAction((click) => {
      const pickedObject = viewer.scene.pick(click.position);
      if (Cesium.defined(pickedObject) && pickedObject.id?.properties?.type?.getValue() === "GROUND_IMAGE") {
        const props = pickedObject.id.properties;
        setSelectedImage({
          name: props.name.getValue(),
          url: props.url.getValue(),
          meta: props.meta.getValue(),
        });
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

    hoverHandlerRef.current = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
    hoverHandlerRef.current.setInputAction((movement) => {
      const pickedObject = viewer.scene.pick(movement.endPosition);
      if (Cesium.defined(pickedObject) && pickedObject.id?.properties?.type?.getValue() === "GROUND_IMAGE") {
        viewer.canvas.style.cursor = "pointer";
      } else {
        viewer.canvas.style.cursor = "default";
      }
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

    return () => {
      if (dataSourceRef.current) {
        try { viewer.dataSources.remove(dataSourceRef.current); } catch (e) {}
        dataSourceRef.current = null;
      }
      if (handlerRef.current) handlerRef.current.destroy();
      if (hoverHandlerRef.current) hoverHandlerRef.current.destroy();
    };
  }, [viewer, images, latestSurvey, apiBase]);


  // --- 5. LISTEN FOR TOGGLE EVENTS FROM SIDEBAR ---
  useEffect(() => {
    if(!viewer) return;

    const handleToggle = (e) => {
      const { id, visible } = e.detail || {};
      // Only process if it looks like one of our IDs
      if (id && id.startsWith("groundimg-") && dataSourceRef.current) {
        const entity = dataSourceRef.current.entities.getById(id);
        if (entity) {
          entity.show = visible;
          viewer.scene.requestRender();
        }
      }
    };

    window.addEventListener("map-toggle-layer", handleToggle);
    return () => {
      window.removeEventListener("map-toggle-layer", handleToggle);
    };
  }, [viewer]);

  // Render Modal
  if (!selectedImage) return null;

  return (
    <div style={modalOverlayStyle} onClick={() => setSelectedImage(null)}>
      <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <span>{selectedImage.name}</span>
          <button style={closeButtonStyle} onClick={() => setSelectedImage(null)}>×</button>
        </div>
        <div style={imageContainerStyle}>
          <img
            src={selectedImage.url}
            alt="Ground Survey"
            style={{ maxWidth: "100%", maxHeight: "60vh", display: "block" }}
          />
        </div>
        <div style={metaStyle}>
          {selectedImage.meta.mtime && <div>Date: {new Date(selectedImage.meta.mtime).toLocaleString()}</div>}
          <div>[{selectedImage.meta.latitude?.toFixed(5)}, {selectedImage.meta.longitude?.toFixed(5)}]</div>
        </div>
      </div>
    </div>
  );
}

// --- Styles (kept same) ---
const modalOverlayStyle = {
  position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: "rgba(0,0,0,0.7)", zIndex: 3000,
  display: "flex", justifyContent: "center", alignItems: "center",
};
const modalContentStyle = {
  backgroundColor: "white", padding: "15px", borderRadius: "8px",
  maxWidth: "90%", maxHeight: "90%", display: "flex", flexDirection: "column", gap: "10px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.5)"
};
const headerStyle = {
  display: "flex", justifyContent: "space-between", alignItems: "center",
  borderBottom: "1px solid #eee", paddingBottom: "8px", fontWeight: "bold", color: "#333"
};
const closeButtonStyle = {
  background: "none", border: "none", fontSize: "24px", cursor: "pointer", color: "#666"
};
const imageContainerStyle = {
  display: "flex", justifyContent: "center", alignItems: "center",
  backgroundColor: "#f0f0f0", minHeight: "200px", borderRadius: "4px"
};
const metaStyle = {
  fontSize: "12px", color: "#666", display: "flex", justifyContent: "space-between", flexWrap: "wrap"
};